/* eslint-disable complexity */
import { type BigintIsh, Price } from '@uniswap/sdk-core'
import {
  nearestUsableTick,
  Position,
  priceToClosestTick,
  TickMath,
  tickToPrice,
} from '@uniswap/v3-sdk'
import * as _ from 'lodash-es'
import { parseUnits } from 'viem'

import { useGetDecimals } from '../blockchain/useGetDecimals'
import { useGetWalletBalance } from '../blockchain/useGetWalletBalance'
import { tokens_to_amount } from '../helpers/block_helpers'
import { convert_to_u_token } from '../helpers/tick_helpers'
import { type FundSymbol, useGetTokenAddress } from '../products'

import { useGetPoolConfig } from './fission_dex_block_queries'

export type PositionUpdates =
  | {
      enabled: false
      isSuccess: false
      outside_current: boolean
      price_low: null
      price_high: null
      deposit_usdc_tokens: null
      deposit_fund_tokens: null
      disabled_usdc: false
      disabled_fund: false
    }
  | {
      enabled: true
      isSuccess: true
      outside_current: boolean
      price_low: number
      price_high: number
      deposit_usdc_tokens: number
      deposit_fund_tokens: number
      disabled_usdc: boolean
      disabled_fund: boolean
    }

// eslint-disable-next-line complexity, max-lines-per-function
export function useGetPositionAmounts({
  fund_symbol,
  price_low,
  price_high,
  deposit_usdc_tokens,
  deposit_fund_tokens,
  active_side,
}: {
  fund_symbol: FundSymbol
  price_low: number
  price_high: number
  deposit_usdc_tokens: number
  deposit_fund_tokens: number
  active_side: 'USDC' | 'fund'
}): PositionUpdates {
  const fund_token_address = useGetTokenAddress({ symbol: fund_symbol })
  const { data: fund_decimals } = useGetDecimals(fund_token_address)
  const usdc_token_address = useGetTokenAddress({ symbol: 'USDC' })
  const { data: usdc_decimals } = useGetDecimals(usdc_token_address)
  const { data: pool } = useGetPoolConfig(fund_symbol)
  const { balance: balance_fund } = useGetWalletBalance(fund_symbol)
  const { balance: balance_usdc } = useGetWalletBalance('USDC')

  const enabled =
    fund_token_address != null &&
    fund_decimals != null &&
    usdc_token_address != null &&
    usdc_decimals != null &&
    pool != null &&
    balance_fund != null &&
    balance_usdc != null

  if (!enabled) {
    return {
      enabled: false as const,
      isSuccess: false as const,
      outside_current: false,
      price_low: null,
      price_high: null,
      deposit_usdc_tokens: null,
      deposit_fund_tokens: null,
      disabled_usdc: false,
      disabled_fund: false,
    }
  }

  if (price_low <= 0) {
    price_low = 1
  }
  if (price_high < price_low) {
    price_high = price_low + 1
  }

  const fund_u_token = convert_to_u_token({
    symbol: fund_symbol,
    address: fund_token_address,
    decimals: fund_decimals,
  })
  const usdc_u_token = convert_to_u_token({
    symbol: 'USDC',
    address: usdc_token_address,
    decimals: usdc_decimals,
  })

  const [token0, token1] = usdc_u_token.sortsBefore(fund_u_token)
    ? [usdc_u_token, fund_u_token]
    : [fund_u_token, usdc_u_token]

  const price_low_ticker = nearestUsableTick(
    priceToClosestTick(
      new Price(
        fund_u_token,
        usdc_u_token,
        parseUnits('1', fund_decimals).toString(),
        parseUnits(price_low.toString(), usdc_decimals).toString(),
      ),
    ),
    pool.tickSpacing,
  )

  const price_high_ticker = nearestUsableTick(
    priceToClosestTick(
      new Price(
        fund_u_token,
        usdc_u_token,
        parseUnits('1', fund_decimals).toString(),
        parseUnits(price_high.toString(), usdc_decimals).toString(),
      ),
    ),
    pool.tickSpacing,
  )

  const [tickLower, tickUpper] =
    price_low_ticker < price_high_ticker
      ? [price_low_ticker, price_high_ticker]
      : [price_high_ticker, price_low_ticker]

  const outside_current = !(
    tickLower <= pool.tickCurrent && pool.tickCurrent <= tickUpper
  )

  if (
    tickLower < TickMath.MIN_TICK ||
    tickUpper > TickMath.MAX_TICK ||
    tickLower == tickUpper
  ) {
    return {
      enabled: false as const,
      isSuccess: false as const,
      outside_current,
      price_low: null,
      price_high: null,
      deposit_usdc_tokens: null,
      deposit_fund_tokens: null,
      disabled_usdc: false,
      disabled_fund: false,
    }
  }
  let amount0: BigintIsh
  let amount1: BigintIsh
  if (active_side == 'USDC') {
    if (token0.symbol == 'USDC') {
      amount0 = tokens_to_amount(
        deposit_usdc_tokens,
        token0.decimals,
      ).toString()
      amount1 = tokens_to_amount(balance_fund, token1.decimals).toString()
    } else {
      amount0 = tokens_to_amount(balance_fund, token1.decimals).toString()
      amount1 = tokens_to_amount(
        deposit_usdc_tokens,
        token0.decimals,
      ).toString()
    }
  } else {
    if (token0.symbol != 'USDC') {
      amount0 = tokens_to_amount(
        deposit_fund_tokens,
        token0.decimals,
      ).toString()
      amount1 = tokens_to_amount(balance_usdc, token1.decimals).toString()
    } else {
      amount0 = tokens_to_amount(balance_usdc, token1.decimals).toString()
      amount1 = tokens_to_amount(
        deposit_fund_tokens,
        token0.decimals,
      ).toString()
    }
  }

  const positionToMint = Position.fromAmounts({
    pool,
    tickLower,
    tickUpper,
    amount0,
    amount1,
    useFullPrecision: true,
  })

  const [updated_deposit_usdc, updated_deposit_fund] =
    positionToMint.amount0.currency.symbol == 'USDC'
      ? [positionToMint.amount0, positionToMint.amount1]
      : [positionToMint.amount1, positionToMint.amount0]

  const disabled_usdc = outside_current && tickLower > pool.tickCurrent
  const disabled_fund = outside_current && pool.tickCurrent > tickUpper

  return {
    enabled: true as const,
    isSuccess: true as const,
    outside_current,
    price_low: Number(
      tickToPrice(fund_u_token, usdc_u_token, price_low_ticker).toFixed(2),
    ),
    price_high: Number(
      tickToPrice(fund_u_token, usdc_u_token, price_high_ticker).toFixed(2),
    ),
    deposit_usdc_tokens: Number(updated_deposit_usdc.toFixed(4)),
    deposit_fund_tokens: Number(updated_deposit_fund.toFixed(4)),
    disabled_usdc,
    disabled_fund,
  }
}
